import type { JsonValue } from "@protobuf-ts/runtime";
import { AccountType, RRSPAccount_PlanType } from "../../generated/core/v1/accounts";
import type { Date as PDate } from "../../generated/core/v1/date";
import { LoanType } from "../../generated/core/v1/debts";
import { MaritalStatus, RetirementMethod } from "../../generated/core/v1/family";
import { PropertyKind } from "../../generated/core/v1/property";
import {
  type AccountsSection_Account,
  type DebtsSection_Debt,
  DefaultDecision_Decision,
  type FieldEdit,
  HouseholdSection_Member_Role as Role,
  Questionnaire,
} from "../../generated/core/v1/questionnaire";
import { Value } from "../../generated/google/protobuf/struct";
import { isSet } from "../lib/dates";
import {
  type AccountKindKey,
  hasDbPension,
  isJobKind,
  MATCHABLE_KINDS,
  members,
  type OnboardingDraft,
} from "./draft";
import { priorityAnswer } from "./fromFamily";
import { jobStartDate } from "./jobStart";
import { debtAnswer, propertyAnswer } from "./owed";

/**
 * Builders for `core.v1.Questionnaire` (architecture-redesign.md item J §6). The client only builds
 * answers; the engine merges them into the family (ApplyQuestionnaire). Nothing here interprets an
 * answer financially: amounts pass through as entered.
 */
export const QUESTIONNAIRE_VERSION = 1;

const ACCOUNT_TYPE: Record<Exclude<AccountKindKey, "credit">, AccountType> = {
  chequing: AccountType.CHEQUING_ACCOUNT,
  savings: AccountType.CHEQUING_ACCOUNT,
  rrsp: AccountType.RRSP_ACCOUNT,
  grouprrsp: AccountType.RRSP_ACCOUNT,
  tfsa: AccountType.TFSA_ACCOUNT,
  fhsa: AccountType.FHSA_ACCOUNT,
  resp: AccountType.RESP_ACCOUNT,
  nonreg: AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT,
};

const MARITAL: Record<OnboardingDraft["partnerStatus"], MaritalStatus> = {
  married: MaritalStatus.MARRIED,
  commonlaw: MaritalStatus.COMMON_LAW,
  single: MaritalStatus.SINGLE,
};

const date = (d: PDate | undefined): PDate | undefined => (isSet(d) ? { ...d } : undefined);

/** An empty questionnaire for partial saves (Update Data, Sharpen, Decisions). */
export function emptyQuestionnaire(asOf?: PDate): Questionnaire {
  return Questionnaire.create({ version: QUESTIONNAIRE_VERSION, asOf: date(asOf) });
}

/** The whole onboarding as one questionnaire: every section set (expected_version 0). */
export function buildOnboardingQuestionnaire(draft: OnboardingDraft, today: PDate): Questionnaire {
  const adults = members(draft);
  const q = emptyQuestionnaire(today);

  q.household = {
    members: adults.map((p, i) => ({
      uuid: p.uuid,
      role: i === 0 ? Role.SELF : Role.PARTNER,
      givenName: p.givenName.trim(),
      familyName: p.familyName.trim(),
      dob: date(p.dob),
    })),
    maritalStatus: MARITAL[draft.partnerStatus],
    children: draft.hasKids
      ? draft.kids.map((k) => ({
          uuid: k.uuid,
          givenName: k.givenName.trim(),
          familyName: k.familyName.trim(),
          dob: date(k.dob),
        }))
      : [],
    province: draft.province,
  };

  const kidUuids = new Set(draft.hasKids ? draft.kids.map((k) => k.uuid) : []);
  q.priorities = {
    priorities: draft.priorities.map((p) => priorityAnswer(p, kidUuids)),
    retirementImportance: draft.retirementImportance ?? 0,
  };

  const memberUuids = new Set(adults.map((p) => p.uuid));
  const owner = (uuid: string) => (memberUuids.has(uuid) ? uuid : draft.self.uuid);

  const incomes = draft.incomes.filter((i) => memberUuids.has(i.memberUuid) && (i.amount ?? 0) > 0);
  // The job a pension is attached to: the one picked, else the first, as the Income step shows it
  // (an income left at zero isn't sent, so it can't be the one).
  const jobOf = (member: string, uuid: string) => {
    const jobs = incomes.filter((i) => i.memberUuid === member && isJobKind(i.kind));
    return (jobs.find((i) => i.uuid === uuid) ?? jobs[0])?.uuid ?? "";
  };
  // "When did you start at ...?" is asked for a defined benefit pension's job only
  const dbJobs = new Set(
    adults
      .filter((p) => hasDbPension(draft, p.uuid))
      .map((p) => jobOf(p.uuid, draft.pensions[p.uuid].jobUuid)),
  );
  q.income = {
    incomes: incomes.map((i) => ({
      uuid: i.uuid,
      memberUuid: i.memberUuid,
      kind: i.kind,
      name: (i.name ?? "").trim(),
      annualAmountBeforeTax: i.amount ?? 0,
      occupation: "",
      groupRrspPlan: MATCHABLE_KINDS.has(i.kind) ? i.groupRrspPlan : undefined,
      startDate: dbJobs.has(i.uuid) && i.start ? jobStartDate(i.start) : undefined,
    })),
  };

  // Every property sends its value; a mortgage only on "Is there a mortgage on it?" yes. A blank
  // rate is sent as not known (the engine estimates it).
  q.properties = { properties: draft.hasProperty ? draft.properties.map(propertyAnswer) : [] };

  const cards = draft.accounts.filter((a) => a.kind === "credit");
  const debts: DebtsSection_Debt[] = cards.map((c) => ({
    uuid: c.uuid,
    kind: LoanType.CREDIT_CARD,
    name: c.name.trim(),
    memberUuid: owner(c.ownerUuid),
    balance: c.balance ?? 0,
    interestRate: c.rate,
  }));
  if (draft.hasDebts) draft.debts.forEach((d) => debts.push(debtAnswer(d, draft.self.uuid)));
  q.debts = { debts };

  q.accounts = {
    accounts: draft.accounts
      .filter((a) => a.kind !== "credit")
      .filter((a) => !(draft.noInvestments && a.kind !== "chequing" && a.kind !== "savings"))
      .map(
        (a): AccountsSection_Account => ({
          uuid: a.uuid,
          kind: ACCOUNT_TYPE[a.kind as Exclude<AccountKindKey, "credit">],
          savings: a.kind === "savings",
          name: a.name.trim(),
          memberUuid: owner(a.ownerUuid),
          balance: a.balance ?? 0,
          beneficiaryUuids:
            a.kind === "resp" ? a.beneficiaryUuids.filter((u) => kidUuids.has(u)) : [],
          interestRate: a.kind === "chequing" || a.kind === "savings" ? a.rate : undefined,
          planType:
            a.kind === "grouprrsp"
              ? RRSPAccount_PlanType.GROUP
              : a.kind === "rrsp"
                ? RRSPAccount_PlanType.INDIVIDUAL
                : RRSPAccount_PlanType.UNSPECIFIED,
        }),
      ),
  };

  q.spending = {
    essentialMonthly: draft.spending.essential ?? 0,
    lifestyleMonthly: draft.spending.lifestyle ?? 0,
    // Rent is kept unless the household owns the home it lives in: owning only a cottage or a
    // rental property still leaves the rent to pay.
    rentMonthly: q.properties.properties.some((p) => p.kind === PropertyKind.PRINCIPAL_RESIDENCE)
      ? undefined
      : draft.spending.rent,
  };

  const cash = new Set(
    draft.accounts.filter((a) => a.kind === "chequing" || a.kind === "savings").map((a) => a.uuid),
  );
  q.safetyNet = {
    emergencyFunds: draft.emergency
      .filter((e) => cash.has(e.accountUuid))
      .map((e) => ({
        accountUuid: e.accountUuid,
        wholeBalance: e.whole,
        amount: e.whole ? undefined : e.amount,
      })),
    // one per adult who said Yes, on the job it's attached to
    pensions: adults.flatMap((p) => {
      const pn = draft.pensions[p.uuid];
      return pn?.has
        ? [{ uuid: pn.uuid, memberUuid: p.uuid, type: pn.type, jobUuid: jobOf(p.uuid, pn.jobUuid) }]
        : [];
    }),
  };

  // "Retiring comfortably": the share of today's income and the age, on the priority itself
  q.retirement = {
    method: RetirementMethod.TARGET,
    incomeReplacementPercent: draft.retirement.pct,
    retirementAge: draft.retirement.age,
  };

  // Not a section answer: each adult's "first-time home buyer" box, checked or not, as a field edit
  // (applied after the sections, so before the engine checks any FHSA's holder).
  q.fieldEdits = adults.flatMap((p) =>
    firstTimeBuyerEdits(
      p.uuid,
      !!draft.firstTimeBuyer[p.uuid],
      draft.accounts.filter((a) => a.kind === "fhsa" && a.ownerUuid === p.uuid).map((a) => a.uuid),
    ),
  );

  return q;
}

// ------------------------------------------------------------------ field edits

/**
 * Marking someone a first-time home buyer (or not), as field edits. Marked one, their FHSAs' move to
 * the RRSP is no longer confirmed (`transfer_to_rrsp_confirmed` false, as the FHSA dialog's "Move"
 * sets it true), so unmarking them later asks about the move again. `fhsaUuids`: their FHSAs being
 * saved (only those whose move is confirmed need it).
 */
export function firstTimeBuyerEdits(
  personUuid: string,
  firstTime: boolean,
  fhsaUuids: string[] = [],
): FieldEdit[] {
  return [
    fieldEdit(paths.personField(personUuid, "is_first_time_home_buyer"), firstTime),
    ...(firstTime
      ? fhsaUuids.map((f) => fieldEdit(paths.fhsaConfirmed(personUuid, f), false))
      : []),
  ];
}

/** A proto3-JSON value as a `google.protobuf.Value`; null clears the field (back to unknown). */
export function toValue(json: JsonValue): Value {
  return Value.fromJson(json);
}

export function fieldEdit(path: string, value: JsonValue): FieldEdit {
  return { path, value: toValue(value) };
}

/** Field paths (the ValueSource format): dotted proto field names, list items by uuid. */
export const paths = {
  person: (uuid: string) => `persons[${uuid}]`,
  personField: (uuid: string, field: string) => `persons[${uuid}].${field}`,
  job: (personUuid: string, jobUuid: string, field: string) =>
    `persons[${personUuid}].jobs[${jobUuid}].${field}`,
  room: (personUuid: string, field: string) => `persons[${personUuid}].registered_room.${field}`,
  fhsaConfirmed: (personUuid: string, fhsaUuid: string) =>
    `persons[${personUuid}].fhsa_accounts[${fhsaUuid}].transfer_to_rrsp_confirmed`,
  oasYears: (personUuid: string) => `persons[${personUuid}].oas_residency.years_in_canada_after_18`,
  retirementGoal: (personUuid: string, field: string) =>
    `persons[${personUuid}].retirement_goal.${field}`,
};

/** "Confirm" / "keep mine" / "take the new one" for the defaults prompts. */
export function defaultDecision(path: string, decision: DefaultDecision_Decision) {
  return { path, decision };
}
export { DefaultDecision_Decision };

/** A questionnaire carrying only field edits (and default decisions). */
export function editsQuestionnaire(
  edits: FieldEdit[],
  asOf?: PDate,
  decisions: { path: string; decision: DefaultDecision_Decision }[] = [],
): Questionnaire {
  const q = emptyQuestionnaire(asOf);
  q.fieldEdits = edits;
  q.defaultDecisions = decisions;
  return q;
}
